import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  adminGetGoogleReviews,
  adminRefreshGoogleReviews,
  type CachedReviewsPayload,
} from "@/lib/googleReviews.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, Star } from "lucide-react";

export const Route = createFileRoute("/tools/reviews")({
  head: () => ({
    meta: [
      { title: "Google Reviews | Apex AV" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ReviewsAdmin,
});

function ReviewsAdmin() {
  const getReviews = useServerFn(adminGetGoogleReviews);
  const refreshReviews = useServerFn(adminRefreshGoogleReviews);

  const [cache, setCache] = useState<CachedReviewsPayload | null>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    getReviews()
      .then((data) => {
        setCache(data);
        if (data?.place_id) setQuery(data.place_id);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [getReviews]);

  async function handleRefresh() {
    if (!query.trim()) {
      toast.error("Enter a Google Maps URL, Place ID, or business name.");
      return;
    }
    setRefreshing(true);
    try {
      const updated = await refreshReviews({ data: { query: query.trim() } });
      setCache(updated);
      toast.success(`Loaded ${updated.reviews.length} reviews for ${updated.place_name}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to refresh reviews");
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl font-bold text-foreground">Google Reviews</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Pull your latest Google reviews (up to 5, per Google's API limit) and display them on the site.
        Paste your Google Maps listing URL, a Place ID, or your business name below and click refresh.
      </p>

      <div className="mt-8 rounded-lg border border-border bg-card p-5 space-y-4">
        <div>
          <Label htmlFor="listing">Google listing</Label>
          <Input
            id="listing"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="https://maps.app.goo.gl/… or ChIJ… or business name"
            className="mt-1"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Tip: open your business on Google Maps and copy the URL, or use{" "}
            <a
              href="https://developers.google.com/maps/documentation/places/web-service/place-id"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Google's Place ID finder
            </a>
            .
          </p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing || loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Fetching…" : "Refresh reviews"}
        </Button>
      </div>

      <div className="mt-8">
        <h2 className="font-display text-xl font-semibold">Cached reviews</h2>
        {loading ? (
          <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
        ) : !cache?.reviews?.length ? (
          <p className="mt-2 text-sm text-muted-foreground">
            No reviews cached yet. Enter your listing above and click refresh.
          </p>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <span className="font-semibold">{cache.place_name}</span>
              <span className="flex items-center gap-1 text-brand-text">
                <Star className="h-4 w-4 fill-current" /> {cache.rating?.toFixed(1)} (
                {cache.user_rating_count})
              </span>
              {cache.place_url && (
                <a
                  href={cache.place_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                >
                  Open on Google <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            {cache.last_refreshed_at && (
              <p className="text-xs text-muted-foreground">
                Last refreshed {new Date(cache.last_refreshed_at).toLocaleString()}
              </p>
            )}
            <ul className="space-y-3">
              {cache.reviews.map((r, i) => (
                <li key={i} className="rounded-md border border-border bg-background p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{r.author}</div>
                    <div className="flex items-center gap-1 text-brand-text text-sm">
                      {Array.from({ length: r.rating }).map((_, k) => (
                        <Star key={k} className="h-3.5 w-3.5 fill-current" />
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">{r.relativeTime}</div>
                  <p className="mt-2 text-sm whitespace-pre-line">{r.text}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
